<template>
  <div id="checkout">
    <v-row justify="center">
      <v-col md="12" xl="10" offset-xl="1">
        <!-- ALERTS -->
        <v-row v-if="currentPlanTranslationKey && currentPlanEndDate && checkoutType === 'downgrade'">
          <v-col cols="12">
            <Alert dense outlined type="warning" icon="warning-system" regular-title>
              <template #title>
                <span
                  v-html="
                    $t('fields.checkout.header_downgrade_plan_info', {
                      current_plan_title: $t(`fields.${currentPlanTranslationKey}`),
                      expire_date: $t(currentPlanEndDate),
                      new_plan_title: selectedPlanTitle,
                    })
                  "
                />
              </template>
            </Alert>
          </v-col>
        </v-row>
        <!-- ALERTS END -->
        <!-- Alert for sub users -->
        <v-row v-if="!isTeamLead">
          <v-col cols="12">
            <AlertSubUserPayment />
          </v-col>
        </v-row>
        <!-- Alert for sub users END -->
        <!-- PAYMENT FORM -->
        <div>
          <Alert
            v-show="formErrorMessage"
            id="errorAlert"
            ref="errorAlert"
            data-test="qr-payment-form-error-alert"
            outlined
            type="error"
            icon="forbidden-system"
          >
            <template #title>
              <div style="display: flex; justify-content: space-between; width: 100%; gap: 20px">
                <span v-text="formErrorMessage" />
                <button
                  v-if="!formStripeErrorMessage"
                  type="button"
                  class="alert-fix-button"
                  @click="scrollToErrorNew(errorContainer)"
                >
                  {{ $t('fields.error.cta_button_fix_it') }}
                </button>
              </div>
            </template>
          </Alert>
          <header-component :checkout-type="checkoutType" :selected-plan="selectedPlan"></header-component>
          <v-row>
            <v-col cols="12" sm="12" md="12" lg="8" xl="8" class="checkout__payment">
              <ValidationObserver ref="observer" slim>
                <v-stepper v-model="stepper" class="checkout__container">
                  <v-stepper-items>
                    <div id="payment-element-container">
                      <v-row justify="center">
                        <v-col md="11">
                          <v-stepper-content step="2" class="checkout__step-container">
                            <!-- Show Payment Element for automatic payments -->
                            <div v-if="!isBankTransferSelected">
                              <h4 class="text-h4 checkout__step-header">
                                {{ $t('fields.checkout.headline_step_2') }}
                              </h4>

                              <div class="checkout__step-description">
                                <div class="mb-4 d-flex">
                                  <Icon name="lock-system" outline :size="24" />
                                  {{ $t('fields.checkout.sub_headline_step_2') }}
                                </div>
                              </div>

                              <PaymentElement
                                ref="paymentElement"
                                :selected-plan="selectedPlan"
                                @switch-to-bank-transfer="setBankTransferSelected(true)"
                              />
                            </div>

                            <!-- Show Bank Transfer info when user selects manual payment -->
                            <div v-else>
                              <h4 class="text-h4 checkout__step-header">
                                {{ $t('fields.checkout.form.invoice_payment') }}
                              </h4>
                              <div class="checkout__step-description">
                                <div class="mb-4 d-flex">
                                  <Icon name="info-system" outline :size="24" />
                                  {{ $t('fields.checkout.form.invoice_info_description') }}
                                </div>
                              </div>

                              <v-btn text :ripple="false" color="primary" @click="setBankTransferSelected(false)">
                                <span class="navy--text d-flex align-center">
                                  <Icon name="arrow-system" outline class="back-arrow-icon mr-1" :size="16" />
                                  {{ $t('fields.checkout.form.back_to_instant') }}
                                </span>
                              </v-btn>
                            </div>
                          </v-stepper-content>
                        </v-col>
                      </v-row>
                    </div>

                    <v-divider class="mb-3" />

                    <!-- STEP 2: BILLING ADDRESS -->
                    <v-stepper-content
                      id="checkoutBillingAddressContainer"
                      step="2"
                      class="checkout__step-container pb-1"
                    >
                      <AddressWithCombobox
                        ref="addressWithCombobox"
                        :stripe-checkout-service="stripeCheckoutService"
                        :team="team"
                      />

                      <email-and-notes :checkout-service="stripeCheckoutService"></email-and-notes>
                    </v-stepper-content>
                    <!-- STEP 2: BILLING ADDRESS END -->

                    <!-- STEP 2: PAYMENT FORM SUBMIT -->
                    <v-stepper-content
                      step="2"
                      class="checkout__step-container checkout__step-container--pb-md-and-down pt-0"
                    >
                      <!-- ORDER SUMMARY (inline above ToS on smaller views) -->
                      <v-row v-if="!$vuetify.breakpoint.lgAndUp" justify="center" class="mt-6">
                        <v-col md="11">
                          <OrderSummary
                            :selected-plan="selectedPlan"
                            :stripe-checkout-service="stripeCheckoutService"
                            :legal-entity="stripeCheckoutService.legalEntity"
                            :checkout-type="checkoutType"
                            borderless
                            @totalPriceChanged="onTotalPriceChanged($event)"
                            @pricePlanChanged="loadSelectedPlan($event)"
                          />
                        </v-col>
                      </v-row>
                      <!-- ORDER SUMMARY END -->
                      <v-row justify="center">
                        <v-col md="11">
                          <CheckoutTosCheckbox
                            v-model="tosConfirmed"
                            checkbox-id="acceptTosCheckbox"
                            :show-error="!tosConfirmed && displayTosCheckboxError"
                            :inline-error="false"
                          />

                          <error-message
                            :invalid="!tosConfirmed && displayTosCheckboxError"
                            class="ml-8 mt-1"
                            data-test="tos-confirmation-checkbox-error"
                          >
                            {{ $t('fields.checkout.form.tos_confirmation_error') }}
                          </error-message>
                        </v-col>
                      </v-row>
                      <v-row justify="center" align="center">
                        <v-col cols="auto">
                          <v-btn
                            depressed
                            rounded
                            :ripple="false"
                            color="primary"
                            x-large
                            data-test="qr-payment-complete-purchase-button"
                            :loading="isSaving"
                            :disabled="!isSubmitPossible"
                            @click="throttleSubmit"
                          >
                            {{ $t('fields.checkout.form.submit_button') }}
                          </v-btn>
                        </v-col>
                      </v-row>

                      <!-- ORDER INFORMATION (below button on smaller views) -->
                      <v-row v-if="!$vuetify.breakpoint.lgAndUp" justify="center" class="mt-4">
                        <v-col md="11" class="py-0">
                          <CheckoutBenefitsList class="py-0" />
                        </v-col>
                      </v-row>
                      <!-- ORDER INFORMATION END -->
                    </v-stepper-content>
                    <!-- STEP 2: PAYMENT FORM SUBMIT END -->

                    <!-- STEP 2: PAYMENT FORM SUBMIT -->
                    <div class="checkout__payment-safety">
                      <v-row justify="center" align="center">
                        <h5 class="text-h5 checkout__payment-safety-header">
                          {{ $t('fields.checkout.form.security_info_header') }}
                        </h5>
                        <p class="checkout__payment-safety-text">
                          {{ $t('fields.checkout.form.security_info_text') }}
                        </p>
                      </v-row>
                    </div>
                    <!-- STEP 2: PAYMENT FORM SUBMIT END -->
                  </v-stepper-items>
                </v-stepper>
              </ValidationObserver>
            </v-col>
            <!-- PAYMENT FORM END -->

            <!-- SIDEBAR (large views only; smaller views render the summary
                 and benefits inline within the payment column) -->
            <PaymentFormSidebar
              v-if="$vuetify.breakpoint.lgAndUp"
              :selected-plan="selectedPlan"
              :stripe-checkout-service="stripeCheckoutService"
              :checkout-type="checkoutType"
              @totalPriceChanged="onTotalPriceChanged($event)"
              @pricePlanChanged="loadSelectedPlan($event)"
            />
            <!-- SIDEBAR END -->
          </v-row>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { planTitleKey } from '@/Services/Subscription/PlanViewService';
import VeeValidateRefs from '@/Models/VeeValidateRefs';
import EventService from '@/Services/EventService';
import { trackCompletePurchaseButtonStep2, trackPaymentFormError } from '@/Services/GoogleTagManager';
import OptimizelyService from '@/Services/OptimizelyService';
import { trackCompletePurchaseButton } from '@/Services/Snowplow/SnowplowService';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import { Team } from '@/classes/auth';
import { Plan, Subscription, SubscriptionState } from '@/classes/stripe';
import Alert from '@/components/Alert.vue';
import AlertSubUserPayment from '@/components/Alerts/AlertSubUserPayment.vue';
import Icon from '@/components/Icons/Icon.vue';
import AddressWithCombobox from '@/components/PaymentForm/AddressWithCombobox.vue';
import EmailAndNotes from '@/components/PaymentForm/EmailAndNotes.vue';
import HeaderComponent from '@/components/PaymentForm/Header.vue';
import PaymentFormSidebar from '@/components/PaymentForm/PaymentFormSidebar.vue';
import OrderSummary from '@/components/PaymentForm/OrderSummary.vue';
import CheckoutBenefitsList from '@/components/PaymentForm/CheckoutBenefitsList.vue';
import PaymentElement from '@/components/PaymentForm/PaymentElement.vue';
import store from '@/store';
import { AuthState } from '@/store/auth';
import { hasPaidIntentSignupMarker } from '@/Services/PaidIntentSignupMarker';
import * as Sentry from '@sentry/vue';
import { debounce } from 'lodash';
import { ValidationObserver } from 'vee-validate';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import { TranslateResult } from 'vue-i18n';
import CheckoutTosCheckbox from '@/components/CheckoutTosCheckbox.vue';
import { PaymentFlowContext, PaymentFlowResult } from '@/Services/PaymentMethod/types';
import { processPayment } from '@/Services/PaymentMethod/processPayment';

@Component({
  components: {
    CheckoutTosCheckbox,
    Icon,
    Alert,
    AlertSubUserPayment,
    AddressWithCombobox,
    PaymentFormSidebar,
    OrderSummary,
    CheckoutBenefitsList,
    HeaderComponent,
    EmailAndNotes,
    PaymentElement,
    ValidationObserver,
  },
  computed: {
    ...mapState<SubscriptionState>('subscription', {
      activeSubscription: (state: SubscriptionState) => state.activeSubscription,
      activePlan: (state: SubscriptionState) => state.activePlan,
      subscriptionPlan: (state: SubscriptionState) => state.selectedPlan,
    }),
    ...mapState<AuthState>('auth', {
      team: (state: AuthState) => new Team(state.team),
    }),
    ...mapGetters('auth', ['isTeamLead']),
  },
})
export default class PaymentForm extends Vue {
  @Prop({ default: '', type: String })
  planId!: string;

  @Prop()
  planName: string | undefined;

  @Prop({ default: '' })
  countryIso2!: string;

  team!: Team;
  activeTab?: any = 0;
  // state from vuex
  activeSubscription!: Subscription;
  // state from vuex
  activePlan!: Plan;
  // state from vuex
  subscriptionPlan!: Plan;
  // getter from vuex
  isTeamLead!: boolean;
  // Handles business logic and form state
  stripeCheckoutService: StripeCheckoutService = new StripeCheckoutService();
  // Handles payment method logic and form state
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();
  // selected subscription plan
  selectedPlan: Plan = new Plan();
  // This is the stepper indicator, actually it is unused, because we fake the steps
  stepper = 2;
  // Prevents to many vat rate calculations
  debouncedVatRateUpdate = debounce(this.updateVatRateForCurrentAddress, 1000, {
    leading: false,
    trailing: true,
  });
  // Prevents double click issues, only last click counts
  throttleSubmit = debounce(this.submit, 300, { leading: false, trailing: true });
  // Prevents double click issues and shows state of saving
  isSaving = false;
  checkoutType = 'new';
  isUpdating = true;

  formErrorMessage = '';
  formStripeErrorMessage = false;

  // needed for Google Apple Pay
  totalPrice: any = 0;
  priceNeedsRecalculation: boolean = this.totalPrice === 0;
  priceHasBeenRecalculated: boolean = !this.priceNeedsRecalculation;
  vatRateUpdateIsPending: boolean = false;
  // Identifies the latest vat lookup, so completions of older ones can be ignored
  vatLookupSeq: number = 0;
  errorContainer = '';

  isBankTransferSelected: boolean = false;

  // Handles payment method logic and form state
  stripeService = this.$store.state.subscription.stripeService;

  $refs!: {
    observer: VeeValidateRefs;
    errorAlert: any;
    addressWithCombobox: AddressWithCombobox;
    paymentElement: PaymentElement;
  };

  // Just legal entities ready for selection
  legalEntityB2C: string = 'b2c';

  tosConfirmed: boolean = false;
  displayTosCheckboxError: boolean = false;

  @Watch('selectedPlan.id', { deep: false, immediate: false })
  onSelectedPlanUpdate(selectedPlan: Plan): void {
    this.subscriptionCheckoutType(selectedPlan.id);
  }

  @Watch('activePlan.id', { deep: true, immediate: false })
  onActivePlanUpdate(): void {
    if (this.subscriptionPlan.id === this.activePlan.id && !this.subScriptionHasIncompletePayment()) {
      this.redirectToPlanSelection();
    }
  }

  // Billing interval requested by a plan-name deep link, e.g. ?interval=month
  get requestedInterval(): string | undefined {
    return (this.$route.query?.interval as string) || undefined;
  }

  get selectedPlanTitle(): TranslateResult {
    return this.$t(planTitleKey(this.selectedPlan));
  }

  // Setup the components needed data, like customer data.
  async created(): Promise<void> {
    this.captureIsPaidIntentSignup();
    await this.loadPlan();
    await this.handlePlanState();

    // Handle country overwrite via URL prop
    if (this.countryIso2.length > 0) {
      await this.stripeCheckoutService.selectCountry(this.countryIso2);
    }

    this.handlePrefillData();
    // set active tab to b2c, if user is b2c
    if (this.team.legal_entity === 'b2c') {
      this.activeTab = 1;
      this.stripeCheckoutService.legalEntity = this.legalEntityB2C;
    }

    // Refresh subscription data from API to avoid stale Vuex state
    if (this.$store.state.auth.user?.id) {
      await this.$store.dispatch('subscription/loadSubscription');
    }

    this.subscriptionCheckoutType(this.selectedPlan.id);

    // All Upgrades and Downgrades should be redirected to pricing page
    const redirectedToPricing = await this.redirectIfUpgradeOrDowngrade();
    if (redirectedToPricing) {
      return;
    }

    this.isUpdating = false;

    EventService.createEvent(EventService.VISITED_CHECKOUT, {
      paramPlanId: this.planId || null,
      paramCountryIso: this.countryIso2 || null,
      // Which tier the user picked, for lifecycle campaign segmentation.
      tierSelected: this.selectedPlan?.planName || null,
    });

    await this.$nextTick();
    this.initOptimizelyTracking();
  }

  async mounted(): Promise<void> {
    await this.$nextTick();
  }

  beforeDestroy(): void {
    this.removeTrackingElement();
  }

  initOptimizelyTracking(): void {
    // Custom event tracking
    OptimizelyService.trackEvent(`checkout_page_view_${this.selectedPlan.planName.toLowerCase()}`);
    this.createTrackingElement();
  }

  setBankTransferSelected(isSelected: boolean): void {
    this.isBankTransferSelected = isSelected;
  }

  async loadPlan(): Promise<void> {
    let plan: Plan | undefined;

    // If there is no selected plan to load, redirect away (CNV-3514)
    if (!this.planId && !this.planName && !plan) {
      window.location.href = '/features-and-pricing';
      return;
    }

    if (this.planId) {
      plan = await this.stripeSubscriptionService.loadPlanById(this.planId);
    }
    if (this.planName) {
      plan = this.stripeSubscriptionService.getStandardPlanByName(this.planName, this.requestedInterval);
    }

    if (plan) await this.$store.dispatch('subscription/setSubscriptionPlan', plan);
  }

  createTrackingElement(): void {
    // We create a hidden input with a class name which includes the plan name
    // We use it for page view tracking inside optimizely dashboard
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('class', `checkout_page_view_${this.selectedPlan.planName.toLowerCase()}`);
    document.body.appendChild(input);
  }

  removeTrackingElement(): void {
    const element = document.querySelector<HTMLElement>(
      `.checkout_page_view_${this.selectedPlan.planName.toLowerCase()}`
    );
    if (element) {
      element.remove(); // Removes the div with the 'div-02' id
    }
  }

  subScriptionHasIncompletePayment(): boolean {
    return ['incomplete', 'past_due'].includes(this.activeSubscription.status);
  }

  async redirectToPlanSelection(): Promise<void> {
    await this.$router.push({ name: 'checkout_plan_selection' });
  }

  // KBPE-46: remember that this checkout session came from the post-signup paid-intent
  // redirect, so an abandoned checkout can send the user to QR-code creation instead of
  // the default account/plan-selection destination. The marker is kept in sessionStorage so it
  // survives a reload of this page.
  captureIsPaidIntentSignup(): void {
    if (this.$route.query.ref === 'marketing_signup' || hasPaidIntentSignupMarker()) {
      this.$store.commit('subscription/setIsPaidIntentSignup', true);
    }
  }

  // Mainly handling of plan state, is a plan selected or not and how to react if not
  async handlePlanState(): Promise<void> {
    // Without a plan (or the same as active) a checkout is not possible so we redirect the customer to the selection page
    if (
      (!this.subscriptionPlan?.id || this.subscriptionPlan?.id === this.activePlan.id) &&
      !this.subScriptionHasIncompletePayment()
    ) {
      await this.redirectToPlanSelection();
    }
  }

  get currentPlanTranslationKey(): string | undefined {
    return this.stripeSubscriptionService.findPlan(this.activeSubscription.plan_id)?.translationKey;
  }

  get currentPlanEndDate(): string {
    return this.activeSubscription.current_period_end.format('MMMM D, YYYY');
  }

  // We fill the form for the customer to ease the pain of filling out
  handlePrefillData(): void {
    // Load plan from vuex
    Object.assign(this.selectedPlan, new Plan(this.subscriptionPlan));

    // Init customer data for prefilling the form
    this.stripeCheckoutService.initCustomerData();

    // Setup subscription for the service
    this.stripeSubscriptionService.initSubscription();
  }

  /**
   * How this works:
   * - Create a subscription with incomplete status
   * - Use intent from the latest_invoice of this subscription and hook into stripe element payment form
   * - Trigger a card payment and save the payment method in stripe
   * - Save payment method as well in our API // TODO can later on be outsourced to webhooks maybe
   * - Redirect on success, stay on failure
   */
  async submit(): Promise<void> {
    const language = store.state?.auth?.user?.language;
    const user_id = store.state?.auth?.user?.id;
    const account = store.state?.account?.account;
    const price_currency_displayed = store.state?.auth?.team?.currency;
    trackCompletePurchaseButton({
      industry_id: account?.industry_id ?? null,
      user_id,
      price_currency_displayed,
      language_code: language,
    });
    trackCompletePurchaseButtonStep2(window.dataLayer, this.selectedPlan.planName, this.selectedPlan.price.price);

    this.formStripeErrorMessage = false;
    this.formErrorMessage = '';

    if ((await this.$refs.observer.validate()) && this.selectedPlan.id && this.isSubmitPossible) {
      this.displayTosCheckboxError = !this.tosConfirmed;

      if (this.displayTosCheckboxError) {
        this.formErrorMessage = this.$t('fields.checkout.form.tos_confirmation_error').toString();
        this.errorContainer = 'acceptTosCheckbox';
        this.scrollToErrorNew(this.errorContainer);
        return;
      }

      // Never charge without a tax amount confirmed by the tax service for the address in the form
      // (blocks while a lookup is pending or in flight, not only after taxLookupFailed).
      if (!this.isTaxConfirmedForCurrentAddress) {
        this.formErrorMessage = this.$t('fields.checkout.form.tax_calculation_failed').toString();
        this.errorContainer = 'checkoutBillingAddressContainer';
        this.scrollToError(this.$refs.errorAlert);
        return;
      }

      // Submit the Elements (collects customer input)
      if (!this.isBankTransferSelected) {
        const submitResult = await this.$refs.paymentElement.elements?.submit();
        if (submitResult?.error) {
          this.errorContainer = 'payment-element-container';
          this.scrollToErrorNew(this.errorContainer);
          return;
        }
      }

      if (this.stripeCheckoutService.location.vat_rate === null) {
        Sentry.withScope((scope) => {
          scope.setExtra('country', this.stripeCheckoutService.location);
          Sentry.captureMessage('[error] dbip fail');
        });

        this.formErrorMessage = this.$t('fields.checkout.form.invalid_tax').toString();
        this.scrollToErrorNew('checkoutPaymentInformationContainer');
        return;
      }

      this.isSaving = true;
      this.$store.commit('layout/toggleGlobalLoader', true);
      this.$store.commit('layout/toggleGlobalOverlay', true);

      try {
        // make sure payment elements is ready before continuing
        if (!this.isBankTransferSelected) {
          if (!this.$refs.paymentElement?.isReady) {
            // If the Payment Elements form is not ready, we cannot continue
            this.formErrorMessage = this.$t('fields.checkout.payment_element.not_ready').toString();
            this.errorContainer = 'payment-element-container';
            this.scrollToErrorNew(this.errorContainer);
            return;
          }
        }

        const context = this.buildPaymentFlowContext();
        const result: PaymentFlowResult = await processPayment(context);

        if (!result.success && result.error) {
          this.formErrorMessage = this.$t(`fields.${result.error.message_key}`, {
            error_code: result.error.error_code,
          }).toString();
          this.errorContainer = result.error.container || '';
          this.scrollToError(this.$refs.errorAlert);
        }
      } catch (error) {
        Sentry.captureException(error);
        this.formErrorMessage = this.$t('fields.checkout.form.invalid').toString();
        this.scrollToError(this.$refs.errorAlert);

        // Refresh the SetupIntent for retry
        if (!this.isBankTransferSelected && this.$refs.paymentElement) {
          await this.$refs.paymentElement.reinitialize();
        }
      } finally {
        this.isSaving = false;
        this.$store.commit('layout/toggleGlobalLoader', false);
        this.$store.commit('layout/toggleGlobalOverlay', false);
      }
    } else {
      this.checkAddressErrors();
      this.errorContainer = 'checkoutBillingAddressContainer';
    }

    if (this.formErrorMessage !== '') {
      trackPaymentFormError(window.dataLayer, this.selectedPlan.planName);
      this.scrollToError(this.$refs.errorAlert);
    }
  }

  buildPaymentFlowContext(): PaymentFlowContext {
    return {
      stripeCheckoutService: this.stripeCheckoutService,
      stripeSubscriptionService: this.stripeSubscriptionService,
      stripeService: this.stripeService,
      selectedPlan: this.selectedPlan,
      checkoutType: this.checkoutType,
      router: this.$router,
      stripe: this.isBankTransferSelected ? null : this.$refs.paymentElement?.stripe,
      elements: this.isBankTransferSelected ? null : this.$refs.paymentElement?.elements,
      selectedPaymentMethodType: this.isBankTransferSelected
        ? 'bank_transfer'
        : this.$refs.paymentElement?.paymentMethodType || '',
    };
  }

  scrollToErrorNew(id: string, topNegativeOffset: number = -150): void {
    // document?.getElementById('errorAlert')?.classList.add('alert--fixed');
    const yOffset = topNegativeOffset;
    const element = document.getElementById(id);
    if (element) {
      const y = (element as HTMLElement).getBoundingClientRect()?.top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  // Scroll to the nearest error from submit upwards
  scrollToError(element: any = null): void {
    if (element) {
      this.$vuetify.goTo(element, { offset: 240 });
    } else {
      const entry: any = Object.keys(this.$refs.observer.errors)
        .reverse()
        .find((entry: any) => {
          return this.$refs.observer.errors[entry].length > 0;
        });

      if (typeof entry !== 'undefined' && entry in this.$refs) {
        //@ts-ignore
        this.$vuetify.goTo(this.$refs[entry], { offset: 240 });
      }
    }
  }

  // No saving process going on and no vat rate update pending or in flight?
  get isSubmitPossible(): boolean {
    return (
      !this.isSaving && !this.stripeCheckoutService.location.showLoading.loadingVatRate && !this.vatRateUpdateIsPending
    );
  }

  // The tax service confirmed a tax amount for the address currently in the form.
  get isTaxConfirmedForCurrentAddress(): boolean {
    return this.stripeCheckoutService.isTaxConfirmed && !this.vatRateUpdateIsPending;
  }

  // Sets the checkout-type (downgrade, upgrade, new)
  subscriptionCheckoutType(planId: string): void {
    const isUpgrade = this.stripeSubscriptionService.isPlanSuperior(planId);

    if (isUpgrade !== null) {
      this.checkoutType = isUpgrade ? 'upgrade' : 'downgrade';
      return;
    }

    const isUpgradeEnterprisePlan = this.stripeSubscriptionService.isEnterprisePlanSuperior(this.subscriptionPlan);

    if (this.subscriptionPlan.id === planId && isUpgradeEnterprisePlan !== null) {
      this.checkoutType = isUpgradeEnterprisePlan ? 'upgrade' : 'downgrade';
    }
  }

  onTotalPriceChanged(price: string): void {
    if (price !== this.totalPrice) {
      this.totalPrice = price;
      this.priceHasBeenRecalculated = true;
    }
  }

  // Keeps the selected plan in sync when the order summary changes the
  // billing period (from either the sidebar or the inline placement).
  loadSelectedPlan(plan: Plan): void {
    Object.assign(this.selectedPlan, new Plan(plan));
  }

  get vatTrigger(): string {
    return JSON.stringify({
      address: this.stripeCheckoutService.customer.address,
      taxId: this.stripeCheckoutService.customer.tax_id_data?.value,
      legalEntity: this.stripeCheckoutService.legalEntity,
    });
  }

  @Watch('vatTrigger', { immediate: false })
  onVatTriggerChange(): void {
    this.handleVatRateUpdate();
  }

  handleVatRateUpdate(): void {
    this.isUpdating = true;
    // The update only runs after the debounce, so the vat rate we hold is already outdated here.
    this.vatRateUpdateIsPending = true;
    this.vatLookupSeq += 1;
    this.debouncedVatRateUpdate(this.vatLookupSeq);
    setTimeout(() => {
      this.isUpdating = false; // Set to false after 1 second to prevent fast flashing of loader
    }, 1000);
  }

  // Looks up the vat rate for the address in the form. Only the latest lookup may clear the pending
  // state, so a slower lookup for an older address cannot resolve on top of a newer one.
  async updateVatRateForCurrentAddress(seq: number): Promise<void> {
    const updatedForVatTrigger = this.vatTrigger;
    try {
      await this.stripeCheckoutService.updateCustomerVatRate();
    } catch (error) {
      // A failed lookup must not leave the form stuck on pending, so we still clear it below.
      Sentry.captureException(error, {
        tags: {
          method: 'updateVatRateForCurrentAddress',
        },
      });
    } finally {
      if (seq === this.vatLookupSeq && this.vatTrigger === updatedForVatTrigger) {
        this.vatRateUpdateIsPending = false;
      }
    }
  }

  checkAddressErrors(): void {
    const addressComponent = this.$refs.addressWithCombobox;
    const errorMessage = addressComponent ? addressComponent.getAddressFieldFirstError() : '';
    this.formErrorMessage = errorMessage || this.$t('fields.checkout.form.invalid').toString();
  }

  /**
   * Redirects upgrade/downgrade deep links back to pricing (with ?plan=),
   * so the pricing page can auto-open the modal for the requested plan.
   */
  private async redirectIfUpgradeOrDowngrade(): Promise<boolean> {
    const targetPlanId = this.planId || this.selectedPlan?.id;
    if (!targetPlanId) {
      return false;
    }

    if (this.checkoutType !== 'upgrade' && this.checkoutType !== 'downgrade') {
      return false;
    }

    if (this.stripeSubscriptionService.isPlanActive(targetPlanId)) {
      return false;
    }

    // enterprise plans still keep the old upgrade/downgrade flow
    if (this.selectedPlan.isEnterprisePlan) {
      return false;
    }

    const nextQuery = { ...this.$route.query, plan: targetPlanId };
    await this.$router.replace({ name: 'checkout_plan_selection', query: nextQuery });
    return true;
  }
}
</script>

<style lang="scss">
#checkout {
  .checkout {
    // Container, which includes payment and summary
    &__container {
      border-radius: 12px;
      box-shadow: none;
      background: map-get($neutral, 'lighten-4');
    }

    // Container with all payment-fields
    &__payment {
      @media (max-width: 600px) {
        padding: 12px 0;
      }
    }

    // Container with Summary and subscription-plan select
    &__summary {
      // Keep the order summary in view while scrolling the payment form.
      // Offset by the fixed 72px app bar height plus a small gap.
      &-sticky {
        @media (min-width: 1264px) {
          position: sticky;
          top: 96px;
        }
      }

      &-container {
        border-radius: 12px;
        border: 2px solid map-get($neutral, 'lighten-2');
        padding: 16px 20px;

        &--borderless {
          border: none;
          padding: 0;
          background: transparent;
        }
      }
    }

    // Header on top of page
    &__header {
      margin-bottom: 20px;

      &--light {
        font-weight: map-get($font-weights, 'regular');
        color: map-get($lapis, 'lighten-2');
      }
    }

    // Header of payment-step
    &__step {
      &-container {
        padding: 20px 40px 40px;

        @media (max-width: 600px) {
          padding: 14px 16px 16px;
        }

        &--pb-md-and-down {
          @media (max-width: 1263px) {
            padding-bottom: 16px;
          }
        }

        .v-stepper__wrapper {
          overflow: visible;
        }
      }

      &-header {
        margin-bottom: 8px;
      }

      &-description {
        display: flex;
        justify-content: space-between;

        @media (max-width: 600px) {
          flex-direction: column;
          align-items: flex-start;
        }

        svg {
          margin-right: 6px;
        }
      }
    }

    // Container below the inputs
    &__payment-safety {
      background-color: map-get($navy, 'base');
      background-image: url('../../assets/images/checkout/lock-image.svg');
      background-repeat: no-repeat;
      background-position: -40px center;
      padding: 28px 40px 40px;
      text-align: center;

      &-header {
        color: map-get($neutral, 'lighten-4');
        margin-bottom: 8px;
        width: 100%;
      }

      &-text {
        color: map-get($primary, 'lighten-1');
        max-width: 416px;
        margin: 0;
      }
    }

    &__notes-wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 8px;
    }

    &__notes-hint {
      font-size: 12px;
      font-style: italic;
      color: map-get($neutral, 'darken-4');
    }
  }

  // Radio buttons
  .radio-selection {
    &__type {
      height: 40px;
      border-radius: 4px;
      border: 1px solid rgba(map-get($neutral, 'darken-3'), 0.38);
      background: #ffffff;

      &:hover {
        border: 1px solid rgba(map-get($neutral, 'darken-3'), 0.86);
      }

      // Make space around radio-icon clickable by expanding the wrapper
      .v-input--selection-controls__input {
        margin: 0;
        height: 40px;
        width: 40px;

        input {
          min-height: 40px;
        }

        // adjust the ripple
        .v-input--selection-controls__ripple {
          left: 0;
          top: calc(50% - 20px);
        }
      }

      label {
        display: flex;
        justify-content: space-between;
        font-size: 18px;
        height: 100%;
        font-weight: map-get($font-weights, 'semi-bold');
        color: map-get($lapis, 'lighten-2');
        padding-right: 12px;
      }

      &.v-item--active {
        border: 1px solid map-get($navy, 'base');

        &:hover {
          box-shadow: 0 0 0 1px map-get($navy, 'base');
        }

        label {
          color: map-get($navy, 'base');
        }
      }
    }

    // radio-buttons for plan-selection
    &--plan {
      padding: 0;
      margin: 0 0 4px;

      .v-messages {
        display: none;
      }

      label {
        font-size: 16px;
        height: 100%;
        font-weight: map-get($font-weights, 'regular');
        color: map-get($lapis, 'lighten-2') !important;
      }

      .radio-selection {
        &__price {
          font-weight: map-get($font-weights, 'semi-bold');
          color: map-get($navy, 'base');
        }
      }
    }
  }

  .input_validation {
    font-size: 12px;
    padding-top: 38px;
    top: 8px;
    left: 0;
    color: map-get($begonia, 'base');
  }

  .input-validation-vat-id {
    margin-bottom: -26px;
  }
}

.cvv-modal__image {
  max-width: 360px;
  width: 100%;
  margin-bottom: 48px;
}

.sepa__mandate {
  font-size: 12px;
  line-height: 18px;
}

#errorAlert {
  .py-0.col.col-auto {
    width: 100%;
  }
}

.alert--fixed {
  position: sticky;
  top: 100px;
  z-index: 9999;
  width: 100%;
  background-color: white !important;
}

.alert-fix-button {
  color: map-get($navy, 'base');
  padding-right: 12px;

  &::after {
    content: '';
    position: relative;
    float: left;
    background: #00bfff;
    width: 100%;
    border-radius: 3px;
    top: 0;
    height: 2px;
  }

  &:hover,
  &:focus {
    color: map-get($navy, 'darken-1');
  }
}

.credit-card-wrapper {
  padding-top: 0 !important;
  padding-bottom: 24px !important;

  @media (max-width: 680px) {
    padding-bottom: 28px !important;
  }
}

.back-arrow-icon {
  transform: rotate(180deg);
}
</style>
